import {useCallback, useMemo} from 'react'
import type {Action} from '@github-ui/action-bar'
import {ActionBar} from '@github-ui/action-bar'
import type {AgentDropdownRepository} from '@github-ui/copilot-chat-types'
import type {ActiveTab} from '../../types/filter-types'
import type {FilterOption, SortDirection} from '../../utils/filter-utils'
import {
  FILTER_STATUS_QUEUED,
  getStatusOptionsForTab,
  getConsolidatedStatusOptionsForTab,
  selectedAgentsAllowQueued,
  TYPE_OPTIONS,
} from '../../utils/filter-utils'
import {SortFilter} from './SortFilter'
import {MultiSelectFilter} from './MultiSelectFilter'
import {AgentMultiSelectFilter} from './AgentMultiSelectFilter'
import {CreatorSelectPanel} from './CreatorSelectPanel'
import {RepoFilterPicker} from '../agents-view/RepoFilterPicker'
import {isFeatureEnabled} from '@github-ui/feature-flags'

export interface FilterActionBarProps {
  activeTab: ActiveTab
  owner?: string
  repo?: string
  sortDirection?: SortDirection
  onSortChange?: (direction: SortDirection) => void
  creatorOptions?: FilterOption[]
  selectedCreators?: string[]
  onCreatorChange?: (creators: string[]) => void
  selectedStatuses?: string[]
  onStatusChange?: (statuses: string[]) => void
  agentOptions?: FilterOption[]
  selectedAgents?: string[]
  onAgentChange?: (agents: string[]) => void
  selectedTypes?: string[]
  onTypeChange?: (types: string[]) => void
  /** Options for the Type filter dropdown. Defaults to the static TYPE_OPTIONS when omitted. */
  typeOptions?: FilterOption[]
  filterButtonClassName?: string
  /**
   * Current repository filter (NWO, "none", or undefined for all repos). Only used when
   * `onRepoFilterChange` is provided.
   */
  repoFilter?: string
  /**
   * When provided, renders a repository picker as an action so it participates in the
   * overflow ("...") menu on small viewports.
   */
  onRepoFilterChange?: (repo: AgentDropdownRepository | undefined) => void
}

export function FilterActionBar({
  activeTab,
  owner,
  repo,
  sortDirection,
  onSortChange,
  creatorOptions,
  selectedCreators,
  onCreatorChange,
  selectedStatuses,
  onStatusChange,
  agentOptions,
  selectedAgents,
  onAgentChange,
  selectedTypes,
  onTypeChange,
  typeOptions,
  filterButtonClassName,
  repoFilter,
  onRepoFilterChange,
}: FilterActionBarProps) {
  const hasFilters = Boolean(onSortChange && onStatusChange && onAgentChange)
  const resolvedTypeOptions = typeOptions ?? TYPE_OPTIONS
  const consolidatedFiltersEnabled = isFeatureEnabled('copilot_mission_control_filters_followups')
  const statusOptions = consolidatedFiltersEnabled
    ? getConsolidatedStatusOptionsForTab(activeTab, selectedTypes || [], selectedAgents || [], agentOptions || [])
    : getStatusOptionsForTab(activeTab)

  // When type or agent filter changes to exclude cloud, clear "queued" from selected statuses
  const handleTypeChangeWithCleanup = useCallback(
    (types: string[]) => {
      if (onTypeChange) onTypeChange(types)
      if (consolidatedFiltersEnabled && onStatusChange && selectedStatuses?.includes(FILTER_STATUS_QUEUED)) {
        const typeAllowsQueued = types.length === 0 || types.includes('cloud')
        if (!typeAllowsQueued || !selectedAgentsAllowQueued(selectedAgents || [], agentOptions || [])) {
          onStatusChange(selectedStatuses.filter(s => s !== FILTER_STATUS_QUEUED))
        }
      }
    },
    [consolidatedFiltersEnabled, onTypeChange, onStatusChange, selectedStatuses, selectedAgents, agentOptions],
  )

  const handleAgentChangeWithCleanup = useCallback(
    (agents: string[]) => {
      if (onAgentChange) onAgentChange(agents)
      if (consolidatedFiltersEnabled && onStatusChange && selectedStatuses?.includes(FILTER_STATUS_QUEUED)) {
        if (!selectedAgentsAllowQueued(agents, agentOptions || [])) {
          onStatusChange(selectedStatuses.filter(s => s !== FILTER_STATUS_QUEUED))
        }
      }
    },
    [consolidatedFiltersEnabled, onAgentChange, onStatusChange, selectedStatuses, agentOptions],
  )

  const actions: Action[] = useMemo(() => {
    if (!hasFilters) return []

    const filterActions: Action[] = []

    if (onCreatorChange && owner && repo) {
      filterActions.push({
        key: 'creator',
        render: (isOverflowMenu: boolean) => (
          <CreatorSelectPanel
            creatorOptions={creatorOptions || []}
            selectedCreators={selectedCreators || []}
            onCreatorChange={onCreatorChange}
            owner={owner}
            repo={repo}
            nested={isOverflowMenu}
            className={!isOverflowMenu ? filterButtonClassName : undefined}
          />
        ),
      })
    }

    filterActions.push({
      key: 'status',
      render: (isOverflowMenu: boolean) => (
        <MultiSelectFilter
          label="Status"
          selected={selectedStatuses || []}
          options={statusOptions}
          onSelectionChange={onStatusChange as (values: string[]) => void}
          testId="status-filter-button"
          className={!isOverflowMenu ? filterButtonClassName : undefined}
          nested={isOverflowMenu}
        />
      ),
    })

    if (onRepoFilterChange) {
      filterActions.push({
        key: 'repo',
        render: (isOverflowMenu: boolean) => (
          <RepoFilterPicker
            repoFilter={repoFilter}
            onFilterChange={onRepoFilterChange}
            nested={isOverflowMenu}
            className={!isOverflowMenu ? filterButtonClassName : undefined}
          />
        ),
      })
    }

    if (onTypeChange && resolvedTypeOptions.length > 0) {
      filterActions.push({
        key: 'type',
        render: (isOverflowMenu: boolean) => (
          <MultiSelectFilter
            label="Type"
            selected={selectedTypes || []}
            options={resolvedTypeOptions}
            onSelectionChange={handleTypeChangeWithCleanup}
            testId="type-filter-button"
            className={!isOverflowMenu ? filterButtonClassName : undefined}
            nested={isOverflowMenu}
          />
        ),
      })
    }

    if ((agentOptions || []).length > 1) {
      filterActions.push({
        key: 'agent',
        render: (isOverflowMenu: boolean) => (
          <AgentMultiSelectFilter
            agentOptions={agentOptions || []}
            selectedAgents={selectedAgents || []}
            onAgentChange={handleAgentChangeWithCleanup}
            className={!isOverflowMenu ? filterButtonClassName : undefined}
            nested={isOverflowMenu}
          />
        ),
      })
    }

    filterActions.push({
      key: 'sort',
      render: (isOverflowMenu: boolean) => (
        <SortFilter
          sortDirection={sortDirection || 'newest'}
          onSortChange={onSortChange as (direction: SortDirection) => void}
          className={!isOverflowMenu ? filterButtonClassName : undefined}
          nested={isOverflowMenu}
        />
      ),
    })

    return filterActions
  }, [
    hasFilters,
    onCreatorChange,
    creatorOptions,
    selectedCreators,
    owner,
    repo,
    selectedStatuses,
    statusOptions,
    onStatusChange,
    handleTypeChangeWithCleanup,
    onTypeChange,
    selectedTypes,
    resolvedTypeOptions,
    agentOptions,
    selectedAgents,
    handleAgentChangeWithCleanup,
    sortDirection,
    onSortChange,
    filterButtonClassName,
    repoFilter,
    onRepoFilterChange,
  ])

  if (!hasFilters) return null

  return <ActionBar label="Task filters" actions={actions} />
}
